import { AppError } from '@shared/api/error'

export type UserFacingError = {
  title: string
  description: string
}

export type UserFacingErrorOptions = {
  title?: string
  fallback?: string
}

const statusMessages: Record<number, UserFacingError> = {
  401: {
    title: 'Sessão expirada',
    description: 'Entre novamente para continuar.',
  },
  403: {
    title: 'Acesso não permitido',
    description: 'Você não tem permissão para realizar esta ação.',
  },
  404: {
    title: 'Não encontramos o conteúdo',
    description: 'Atualize a tela e tente novamente.',
  },
  409: {
    title: 'Não foi possível concluir',
    description: 'O estado mudou. Atualize a tela e tente novamente.',
  },
  422: {
    title: 'Revise os dados',
    description: 'Confira os campos destacados e tente novamente.',
  },
  429: {
    title: 'Muitas tentativas',
    description: 'Aguarde alguns instantes antes de tentar novamente.',
  },
  500: {
    title: 'Serviço indisponível',
    description: 'Tente novamente em alguns instantes.',
  },
  502: {
    title: 'Serviço indisponível',
    description: 'Tente novamente em alguns instantes.',
  },
  503: {
    title: 'Serviço indisponível',
    description: 'Tente novamente em alguns instantes.',
  },
  504: {
    title: 'Serviço indisponível',
    description: 'Tente novamente em alguns instantes.',
  },
}

const codeMessages: Record<string, UserFacingError> = {
  ProductNotFound: {
    title: 'Não encontramos o produto',
    description: 'Atualize a tela e tente novamente.',
  },
  Unauthorized: statusMessages[401]!,
  Forbidden: statusMessages[403]!,
  Conflict: statusMessages[409]!,
  ValidationError: statusMessages[422]!,
  InternalServerError: statusMessages[500]!,
  ServiceUnavailable: statusMessages[503]!,
}

const defaultMessage: UserFacingError = {
  title: 'Algo deu errado',
  description: 'Não conseguimos concluir esta ação. Tente novamente.',
}

function messageFor(error: unknown): UserFacingError {
  if (!(error instanceof AppError)) return defaultMessage
  return codeMessages[error.code] ?? statusMessages[error.status] ?? defaultMessage
}

export function getUserFacingError(
  error: unknown,
  options: UserFacingErrorOptions = {},
): UserFacingError {
  const message = messageFor(error)
  return {
    title: options.title ?? message.title,
    description: options.fallback ?? message.description,
  }
}

export function getUserFacingErrorMessage(error: unknown, fallback: string): string {
  return getUserFacingError(error, { fallback }).description
}

// Provider messages and unknown codes must never reach the administrative UI.
export function getPayoutFailureMessage(code: string | null | undefined): string {
  switch (code) {
    case 'account_closed':
      return 'A conta bancária de destino foi encerrada. Confira os dados bancários no fluxo Connect do participante.'
    case 'account_frozen':
      return 'A conta bancária está bloqueada.'
    case 'bank_account_restricted':
      return 'O banco restringiu o recebimento nesta conta.'
    case 'bank_ownership_changed':
      return 'A titularidade da conta bancária mudou.'
    case 'could_not_process':
      return 'O banco não conseguiu processar o recebimento.'
    case 'debit_not_authorized':
      return 'O banco não autorizou a operação.'
    case 'declined':
      return 'O banco recusou o recebimento.'
    case 'insufficient_funds':
      return 'O provedor informou saldo insuficiente.'
    case 'invalid_account_number':
      return 'Os dados da conta bancária precisam ser revisados. Confira os dados bancários no fluxo Connect do participante.'
    case 'no_account':
      return 'A conta bancária não foi localizada.'
    case 'invalid_currency':
      return 'A conta bancária não aceita a moeda do recebimento.'
    default:
      return 'O motivo da falha não está disponível. Confira o cadastro e os dados bancários no fluxo Connect do participante.'
  }
}
